import { Badge } from "@bcc/components/ui/badge";
import { Card, CardContent } from "@bcc/components/ui/card";
import { AlertTriangle, CheckCircle, LucideIcon, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  setNotificationsViewDescriptionTestId,
  setNotificationsViewListTestId,
  setNotificationsViewTestId,
  setNotificationsViewTitleTestId,
  setNotificationsViewUnreadBadgeTestId,
} from "./notifications-view.utils";

interface NotificationData {
  id: number;
  type: "order" | "alert" | "success";
  title: string;
  message: string;
  time: string;
  icon: string;
  unread: boolean;
}

interface NotificationsViewProps {
  notificationsData: NotificationData[];
  testId?: string;
}

const iconMap: Record<string, LucideIcon> = {
  Package,
  AlertTriangle,
  CheckCircle,
};

const typeConfig = {
  order: {
    bgColor: "bg-blue-100 dark:bg-blue-950",
    iconColor: "text-blue-700 dark:text-blue-400",
  },
  alert: {
    bgColor: "bg-amber-100 dark:bg-amber-950",
    iconColor: "text-amber-700 dark:text-amber-400",
  },
  success: {
    bgColor: "bg-primary/10",
    iconColor: "text-primary",
  },
};

interface NotificationCardProps {
  notification: NotificationData;
}

function NotificationCard({ notification }: NotificationCardProps) {
  const IconComponent = iconMap[notification.icon] || Package;
  const config = typeConfig[notification.type];

  return (
    <Card className={notification.unread ? "border-primary/50" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${config.bgColor}`}>
            <IconComponent className={`h-5 w-5 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium">{notification.title}</p>
              {notification.unread && <div className="h-2 w-2 rounded-full bg-primary" />}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
            <p className="text-xs text-muted-foreground mt-2">{notification.time}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function NotificationsView({ notificationsData, testId }: NotificationsViewProps) {
  const { t } = useTranslation();
  const unreadCount = notificationsData.filter((n) => n.unread).length;

  return (
    <div data-testid={testId ?? setNotificationsViewTestId("container")} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid={setNotificationsViewTitleTestId()} className="text-3xl font-bold tracking-tight text-balance">
            {t("views.notifications.title")}
          </h1>
          <p data-testid={setNotificationsViewDescriptionTestId()} className="mt-2 text-muted-foreground">
            {t("views.notifications.description")}
          </p>
        </div>
        <Badge data-testid={setNotificationsViewUnreadBadgeTestId()} variant="secondary" className="bg-primary/10 text-primary">
          {t("views.notifications.new", { count: unreadCount })}
        </Badge>
      </div>

      <div data-testid={setNotificationsViewListTestId()} className="space-y-3">
        {notificationsData.map((notification) => (
          <NotificationCard key={notification.id} notification={notification} />
        ))}
      </div>
    </div>
  );
}
